import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  RequestError,
  webhooksApi,
  type WebhookCreateResponse,
  type WebhookItem,
  type WebhookKind,
} from '../api/client';
import {
  buildWebhookCreateBody,
  CHATWORK_TOKEN_MAX,
  validateWebhookForm,
  WEBHOOK_EVENTS,
  WEBHOOK_KINDS,
  webhookEventKey,
} from '../utils/webhooks';

function formatDateJst(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * 通知先の設定（課題 #113）。検知があったときに Chatwork / Slack / Teams などへ知らせる先を登録・失効する。
 *
 * - 登録時は必ずテスト通知を 1 通送って届くかを確かめる（2026-09-24 CENTRIC 決定）。
 *   Webhook URL は打ち間違えても登録時には何も起こらず、通知が必要になったときに
 *   初めて「届いていない」と分かるため
 * - 届かなかった登録は無効の状態で残る。一覧で「無効」と分かるようにする
 * - URL はそれ自体が認証情報なので、一覧では末尾だけを出す（BE が伏せて返す）
 * - 操作できるのは全社管理者だけ（拠点管理者は 403）
 * - Chatwork は URL の代わりにルーム ID と API トークンで登録する（BE PR #141）。トークンは
 *   BE が暗号化して保存し、一覧には末尾 4 文字だけが返る
 * - 通知する事象は最初は何も選ばない。MCS アラートと送信・文字起こしの失敗は受け取る人が違い、
 *   送り先のルームを分けて使うため（2026-09-29 高尾さん）。全部に付いた状態から外し忘れると、
 *   MCS アラートが障害を見る担当者のルームにも流れてしまう
 */
export default function AdminWebhooks() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [items, setItems] = useState<WebhookItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [kind, setKind] = useState<WebhookKind>(WEBHOOK_KINDS[0]);
  const [url, setUrl] = useState('');
  const [chatworkRoomId, setChatworkRoomId] = useState('');
  const [chatworkToken, setChatworkToken] = useState('');
  const [label, setLabel] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<WebhookCreateResponse | null>(null);

  const [actionId, setActionId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  /** ラベルを書き換え中の行と、その入力 */
  const [labelEditing, setLabelEditing] = useState<{ id: string; value: string } | null>(null);

  const eventLabel = useCallback(
    (event: string) => {
      const key = webhookEventKey(event);
      return key ? t(`admin.webhooks.event.${key}`) : event;
    },
    [t]
  );
  const kindLabel = useCallback(
    (k: string | undefined) =>
      WEBHOOK_KINDS.includes(k as WebhookKind) ? t(`admin.webhooks.kind.${k}`) : (k ?? ''),
    [t]
  );

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setForbidden(false);
    webhooksApi
      .list(token)
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => {
        if (err instanceof RequestError && err.status === 403) setForbidden(true);
        else setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleEvent = (event: string) => {
    setEvents((prev) => (prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token || submitting) return;
    const input = { kind, url, events, chatworkRoomId, chatworkToken, label };
    const invalid = validateWebhookForm(input);
    if (invalid) {
      setFormError(t(`admin.webhooks.form.${invalid}`));
      return;
    }
    setFormError(null);
    setCreated(null);
    setSubmitting(true);
    try {
      const res = await webhooksApi.create(buildWebhookCreateBody(input), token);
      setCreated(res);
      setUrl('');
      setChatworkRoomId('');
      // トークンは認証情報なので、登録できたら画面に残さない
      setChatworkToken('');
      setLabel('');
      load();
    } catch (err) {
      if (err instanceof RequestError && err.status === 403) setFormError(t('admin.webhooks.forbidden'));
      else setFormError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  /** 有効 / 無効の切り替え。無効にすると通知は送られない（失効） */
  const handleToggle = async (item: WebhookItem) => {
    const token = getToken();
    if (!token || actionId) return;
    const next = item.status === 'active' ? 'inactive' : 'active';
    if (next === 'inactive' && !window.confirm(t('admin.webhooks.deactivateConfirm'))) return;
    setActionId(item.id);
    setRowError((prev) => {
      const copy = { ...prev };
      delete copy[item.id];
      return copy;
    });
    try {
      await webhooksApi.update(item.id, { status: next }, token);
      load();
    } catch (err) {
      setRowError((prev) => ({
        ...prev,
        [item.id]: err instanceof Error ? err.message : t('common.error'),
      }));
    } finally {
      setActionId(null);
    }
  };

  /** 削除（BE PR #123）。一覧から消え、元に戻せない。一時的に止めるなら「無効にする」 */
  const handleDelete = async (item: WebhookItem) => {
    const token = getToken();
    if (!token || actionId) return;
    if (!window.confirm(t('admin.webhooks.deleteConfirm'))) return;
    setActionId(item.id);
    try {
      await webhooksApi.remove(item.id, token);
      load();
    } catch (err) {
      setRowError((prev) => ({
        ...prev,
        [item.id]: err instanceof Error ? err.message : t('common.error'),
      }));
    } finally {
      setActionId(null);
    }
  };

  /** ラベルの変更。空にすると消える（BE は空文字で消す） */
  const handleSaveLabel = async () => {
    const token = getToken();
    if (!token || !labelEditing || actionId) return;
    const target = labelEditing;
    setActionId(target.id);
    try {
      await webhooksApi.update(target.id, { label: target.value.trim() }, token);
      setLabelEditing(null);
      load();
    } catch (err) {
      setRowError((prev) => ({
        ...prev,
        [target.id]: err instanceof Error ? err.message : t('common.error'),
      }));
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="page admin-webhooks-page">
      <h1>{t('admin.webhooks.title')}</h1>
      <p className="admin-page-intro">{t('admin.webhooks.intro')}</p>

      {forbidden && (
        <div className="admin-card" role="alert" data-testid="webhooks-forbidden">
          <p>{t('admin.webhooks.forbidden')}</p>
        </div>
      )}

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {!forbidden && (
        <form className="admin-card admin-form" onSubmit={handleCreate} noValidate data-testid="webhook-form">
          <h2>{t('admin.webhooks.form.title')}</h2>
          <fieldset className="admin-webhooks-kind">
            <legend>{t('admin.webhooks.form.kind')}</legend>
            {WEBHOOK_KINDS.map((k) => (
              <label key={k}>
                <input
                  type="radio"
                  name="webhook-kind"
                  value={k}
                  checked={kind === k}
                  onChange={() => setKind(k)}
                />
                {t(`admin.webhooks.kind.${k}`)}
              </label>
            ))}
          </fieldset>
          {kind === 'chatwork' ? (
            <>
              <div className="admin-form-group">
                <label htmlFor="webhook-chatwork-room">{t('admin.webhooks.form.chatworkRoomId')}</label>
                <input
                  id="webhook-chatwork-room"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={chatworkRoomId}
                  onChange={(e) => setChatworkRoomId(e.target.value)}
                  placeholder="123456789"
                />
                <p className="admin-form-hint">{t('admin.webhooks.form.chatworkRoomIdHint')}</p>
              </div>
              <div className="admin-form-group">
                <label htmlFor="webhook-chatwork-token">{t('admin.webhooks.form.chatworkToken')}</label>
                <input
                  id="webhook-chatwork-token"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={CHATWORK_TOKEN_MAX}
                  value={chatworkToken}
                  onChange={(e) => setChatworkToken(e.target.value)}
                />
                <p className="admin-form-hint">{t('admin.webhooks.form.chatworkTokenHint')}</p>
              </div>
            </>
          ) : (
            <div className="admin-form-group">
              <label htmlFor="webhook-url">{t('admin.webhooks.form.url')}</label>
              <input
                id="webhook-url"
                type="url"
                inputMode="url"
                autoComplete="off"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://"
              />
              <p className="admin-form-hint">{t('admin.webhooks.form.urlHint')}</p>
            </div>
          )}
          <div className="admin-form-group">
            <label htmlFor="webhook-label">{t('admin.webhooks.form.label')}</label>
            <input
              id="webhook-label"
              type="text"
              maxLength={100}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <p className="admin-form-hint">{t('admin.webhooks.form.labelHint')}</p>
          </div>
          <fieldset className="admin-webhooks-events">
            <legend>{t('admin.webhooks.form.events')}</legend>
            {WEBHOOK_EVENTS.map((ev) => (
              <label key={ev}>
                <input type="checkbox" checked={events.includes(ev)} onChange={() => toggleEvent(ev)} />
                {eventLabel(ev)}
              </label>
            ))}
            <p className="admin-form-hint">{t('admin.webhooks.form.eventsHint')}</p>
          </fieldset>
          <p className="admin-form-hint">{t('admin.webhooks.form.verifyNote')}</p>
          {formError && (
            <p className="admin-form-error" role="alert">
              {formError}
            </p>
          )}
          <button type="submit" className="admin-btn-primary" disabled={submitting}>
            {submitting ? t('admin.webhooks.form.submitting') : t('admin.webhooks.form.submit')}
          </button>

          {created && (
            <div
              className={`admin-webhooks-result ${created.verified === false ? 'is-failed' : 'is-ok'}`}
              role="status"
              data-testid="webhook-created"
            >
              <p>
                {created.verified === true
                  ? t('admin.webhooks.result.delivered')
                  : created.verified === false
                  ? t(
                      created.kind === 'chatwork'
                        ? 'admin.webhooks.result.notDeliveredChatwork'
                        : 'admin.webhooks.result.notDelivered',
                      { reason: created.verify_error || t('admin.webhooks.result.unknownReason') }
                    )
                  : t('admin.webhooks.result.notVerified')}
              </p>
              {/* 汎用の宛先は署名の検証にこの鍵を使う。この応答でしか受け取れない */}
              {created.kind === 'generic' && created.secret && (
                <div className="admin-webhooks-secret" data-testid="webhook-secret">
                  <p className="admin-form-hint">{t('admin.webhooks.result.secretNote')}</p>
                  <code>{created.secret}</code>
                </div>
              )}
            </div>
          )}
        </form>
      )}

      {loading && (
        <div className="admin-card">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {!loading && !error && !forbidden && (
        <div className="admin-card">
          <h2>{t('admin.webhooks.listTitle')}</h2>
          {items.length === 0 ? (
            <p className="admin-empty">{t('admin.webhooks.empty')}</p>
          ) : (
            <table className="admin-table admin-webhooks-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.webhooks.column.label')}</th>
                  <th scope="col">{t('admin.webhooks.column.kind')}</th>
                  <th scope="col">{t('admin.webhooks.column.url')}</th>
                  <th scope="col">{t('admin.webhooks.column.events')}</th>
                  <th scope="col">{t('admin.webhooks.column.status')}</th>
                  <th scope="col">{t('admin.webhooks.column.createdAt')}</th>
                  <th scope="col">{t('admin.webhooks.column.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const active = item.status === 'active';
                  return (
                    <tr key={item.id} data-testid="webhook-row">
                      <td>
                        {labelEditing?.id === item.id ? (
                          <div className="admin-webhooks-label-edit">
                            <input
                              type="text"
                              maxLength={100}
                              value={labelEditing.value}
                              onChange={(e) => setLabelEditing({ id: item.id, value: e.target.value })}
                              aria-label={t('admin.webhooks.form.label')}
                            />
                            <button
                              type="button"
                              className="admin-btn-text"
                              onClick={handleSaveLabel}
                              disabled={actionId != null}
                            >
                              {t('admin.webhooks.saveLabel')}
                            </button>
                            <button
                              type="button"
                              className="admin-btn-text"
                              onClick={() => setLabelEditing(null)}
                              disabled={actionId != null}
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="admin-webhooks-label">
                              {item.label ? item.label : t('admin.webhooks.labelNone')}
                            </span>{' '}
                            <button
                              type="button"
                              className="admin-btn-text"
                              onClick={() => setLabelEditing({ id: item.id, value: item.label ?? '' })}
                              disabled={actionId != null}
                            >
                              {t('admin.webhooks.editLabel')}
                            </button>
                          </>
                        )}
                      </td>
                      <td>{kindLabel(item.kind)}</td>
                      <td>
                        <code className="admin-webhooks-url">{item.url}</code>
                        {/* Chatwork はトークンの末尾で見分ける（全体は BE も返さない） */}
                        {item.credential_tail && (
                          <span className="admin-webhooks-credential" data-testid="webhook-credential-tail">
                            {t('admin.webhooks.credentialTail', { tail: item.credential_tail })}
                          </span>
                        )}
                      </td>
                      <td>{item.events.map(eventLabel).join('、')}</td>
                      <td>
                        <span className={`admin-webhooks-status ${active ? 'is-active' : 'is-inactive'}`}>
                          {active ? t('admin.webhooks.status.active') : t('admin.webhooks.status.inactive')}
                        </span>
                      </td>
                      <td>{formatDateJst(item.created_at)}</td>
                      <td>
                        <button
                          type="button"
                          className="admin-btn-text"
                          onClick={() => handleToggle(item)}
                          disabled={actionId != null}
                        >
                          {active ? t('admin.webhooks.deactivate') : t('admin.webhooks.activate')}
                        </button>
                        <button
                          type="button"
                          className="admin-btn-text admin-btn-text-danger"
                          onClick={() => handleDelete(item)}
                          disabled={actionId != null}
                        >
                          {t('admin.webhooks.delete')}
                        </button>
                        {rowError[item.id] && (
                          <p className="admin-form-error" role="alert">
                            {rowError[item.id]}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p className="admin-form-hint">{t('admin.webhooks.inactiveHint')}</p>
        </div>
      )}

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
      </p>
    </div>
  );
}
